// BI.1.1 Manage Accounts
import { ClientTable, Event as vuetables2Event } from 'vue-tables-2';
import scrollUp from '../shared/vue/mixins/scrollUp';
import { select, each } from '../shared/utils/dom';
import { showSuccessErrorMessage, getErrorMessages, linkedAccountsApi } from './api';

export default function manageAccounts() {
  const el = select('[data-vue-app="manage-accounts"]');
  if (!el) {
    return;
  }

  // @see https://github.com/matfish2/vue-tables-2

  Vue.use(ClientTable);

  // start data
  const data = {};

  // the url
  data['url'] = `/LinkedAccounts/1.0/AllSummaries`;

  // columns
  data['columns'] = ['accountNickname', 'link'];

  // has loaded data for the first time?
  data['hasMounted'] = true;

  // if the table is loading data from the server
  data['loading'] = false;

  data['enabledLdcs'] = window.VueTables.enabledLdcs;

  data['anyNonDeployedLdc'] = false;

  data['errorMessages'] = getErrorMessages();

  // start options
  const options = {};

  // filterable?
  options['filterable'] = false;

  // Disable sorting (currently not supported on server)
  options['sortable'] = [];

  options['headings'] = {
    accountNickname: 'Account',
    link: '',
  };

  options['rowClassCallback'] = function (row) {
    return `row-account row-${row.accountId}`;
  };

  // how many results per page?
  options['perPage'] = 10;

  // start templates
  const templates = {};

  /**
   * Template construction for "Account Nickname"
   */
  templates.accountNickname = function (h, row) {
    /* eslint-disable */
    return (
      <div class="table-cell-wrapper table-cell-header">
        <div class="table-cell-label">Account</div>
        <div
          class={`table-cell-content account-nickname ${
            row.starred ? "favorite" : ""
            }`}
        >
          {starIcon(h, row)}
          <div>
            <div>
              <strong>{row.accountNickname}</strong>
            </div>
            <div v-if="row.serviceAddressCount === 1">{row.serviceAddressToDisplay}</div>
            <span class="text-gray">#{row.accountId}</span>
          </div>
        </div>
      </div>
    );
    /* eslint-enable */
  };

  /**
   * Function to show different star icons
   */
  function starIcon(h, row) {
    if (row.starred === true) {
      return (
        <svg className="icon icon-star starred" on-click={ () => app.toggleFavorite(event, row) // eslint-disable-line no-use-before-define
        }>
          <use xlinkHref="#icon-star"></use>
        </svg>
      );
    }
    return (
      <svg className="icon icon-star" on-click={ () => app.toggleFavorite(event, row) // eslint-disable-line no-use-before-define
      }>
        <use xlinkHref="#icon-star"></use>
      </svg>
    );
  }

  /**
   * Template for Link Arrow
   */
  templates.link = function (h, row) { // eslint-disable-line no-unused-vars
    return (
      <div>
        <span
          style={{ cursor: 'pointer' }}
        >
          <svg className="icon icon-arrow-down icon-arrow-down--rotate">
            <use xlinkHref="#icon-arrow-down"></use>
          </svg>
        </span>
      </div>
    );
  }

  // set templates into options
  options['templates'] = templates;

  // set options into data
  data['options'] = options;

  const app = new Vue({
    el,
    mixins: [scrollUp],
    data() {
      data['tableData'] = this.getLinkedAccounts();

      return data;
    },
    mounted() {
      if (this.anyNonDeployedLdc) {
        // find and replace list of deployed LDCs in curly brackets
        select('#page-alerts').innerHTML = select('#page-alerts').innerHTML.replace(/{enabled_ldcs}/gi, app.fullLdcNames);
      }

      const element = select('.icon.icon-star');
      element.style.webkitTransform = element.style.webkitTransform;
    },
    created() {
      Event.$on('A11yDialogConfirm:toggle-favorite', () => {
        this.updateAccountFavorite();
      });
      Event.$on('A11yDialogConfirm:toggle-un-favorite', () => {
        this.updateAccountFavorite();
      });
    },
    computed: {
      fullLdcNames() {
        // apparently the data isn't already an array
        const array = this.enabledLdcs.replace(/['"[[\]]+/g, '').split(',');
        const ldcList = [];
        array.forEach(ldc => {
          switch (ldc) {
            case 'OH':
              ldcList.push('Ohio');
              break;
            case 'BS':
              ldcList.push('Massachusetts');
              break;
            case 'NI':
              ldcList.push('Indiana');
              break;
            case 'KY':
              ldcList.push('Kentucky');
              break;
            case 'VA':
              ldcList.push('Virginia');
              break;
            case 'PA':
              ldcList.push('Pennsylvania');
              break;
            case 'MD':
              ldcList.push('Maryland');
              break;
            default:
              ldcList.push('');
              break;
          }
        });
        if (ldcList.length > 2) {
          return `${ldcList.slice(0, ldcList.length - 1).join(', ')}, and ${ldcList.slice(-1)}`;
        } else if (ldcList.length === 2) {
          return `${ldcList.slice(0, ldcList.length - 1)} and ${ldcList.slice(-1)}`;
        }
        return `${ldcList}`;
      },
    },
    methods: {
      getLinkedAccounts() {
        const localData = [];

        each(window.VueTables.linkedAccountsData.linkedAccounts, ({ customerAccountId, linkedAccountNickname, ldc, starred, serviceAddressCount, serviceAddress }) => {
          localData.push({
            accountNickname: linkedAccountNickname,
            // Mange in Legacy system if LDC is not deployed
            manageInLegacySystem: !this.checkLdcDeployStatus(ldc),
            // need the account number to display with nickname
            // this will not display since key not defined in columns
            accountId: customerAccountId,
            ldc, // es6 allows us to not need the `ldc: ldc`
            starred,
            serviceAddressCount,
            serviceAddressToDisplay: serviceAddressCount === 1 ? `${serviceAddress != null && serviceAddress.address1 != null ? serviceAddress.address1 : ''} ${serviceAddress != null && serviceAddress.address2 != null ? serviceAddress.address2 : ''}` : ``,
          });
        });

        // check if there are any non-deployed LDCs loaded
        if (this.anyNonDeployedLdc) { // eslint-disable-line no-use-before-define
          showSuccessErrorMessage(this.errorMessages, "LinkAccount-AccountInNonDeployedLdc"); // eslint-disable-line no-use-before-define
        }

        return localData;
      },
      checkLdcDeployStatus(ldc) {
        if (window.VueTables.enabledLdcs.indexOf(ldc) === -1) { // eslint-disable-line no-use-before-define
          this.anyNonDeployedLdc = true; // eslint-disable-line no-use-before-define
          return false;
        }
        return true;
      },
      toggleFavorite(event, row) {
        event.stopPropagation();
        this.selectedRow = row;
        if (row.starred) {
          Event.$emit('A11yDialogOpen', { dialogKey: 'toggle-un-favorite' });
        } else {
          Event.$emit('A11yDialogOpen', { dialogKey: 'toggle-favorite' });
        }
      },
      updateAccountFavorite() {
        const row = this.selectedRow;
        this.loading = true;
        const requestData = {
          accountNickname: row.accountNickname,
          starred: !row.starred,
        };
        linkedAccountsApi.updateAccountProfile(row.accountId, requestData)
          .then(() => {
            this.loading = false;
            const starredRow = this.tableData.find((item) => item.starred === true);
            if (starredRow != null) {
              starredRow.starred = false;
            }
            if (starredRow == null || starredRow.accountId !== row.accountId) {
              const selectedRow = this.tableData.find((item) => item.accountId === row.accountId);
              if (selectedRow != null) {
                selectedRow.starred = true;
              }
            }
            linkedAccountsApi.updateFavoriteAccount(row.accountId, null, !row.starred);
          })
          .catch((err) => {
            console.log(err);
            this.loading = false;
            this.scrollUp();
          });
      },
    },
  });

  // this will remove the initial loading state and show the table
  vuetables2Event.$on('vue-tables.error', () => {
    app.hasMounted = true;
    app.loading = false;
  });

  vuetables2Event.$on('vue-tables.row-click', (row) => {
    if (row.row.manageInLegacySystem) {
      window.location.href = `/wss/${row.row.ldc}`;
    } else {
      window.location.href = `/account/manage/${row.row.accountId}`;
    }
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/manageAccounts.js